/**
 * The minerals knowledge model (M8 plan MC-a7a): what the player knows about a system's minerals. Nothing until the
 * system is `charted` (by a visit, `markCharted`, or the new-game prechart), then its signature (D-485/D-489) and the
 * grade of its asteroid deposits. A rumoured or unknown system is `'uncharted'`. Pure over the Reach state and the
 * generated galaxy: knowledge comes from `ReachState.knowledge`, the minerals from the galaxy's generated keys
 * (`StarSystem.signature`, `Field.grade`, `Field.mineralWeights ?? oreWeights`), so it survives a reload unchanged.
 * Rare (UI and planning reads): allocates freely.
 */
import { galaxyGradeIds, galaxyOres, type GalaxyGradeId } from '../data/galaxyConfig'
import type { Galaxy, MineralOre, SystemSignature } from '../galaxy/types'
import { galaxyIndex } from '../reach/state/galaxyIndex'
import type { ReachState } from '../reach/types'

const isMineralOre = (o: (typeof galaxyOres)[number]): o is MineralOre => o !== 'tetherstone'
/** The ores the model reports, in galaxy ore order. Tetherstone is never listed (it is in no grade or signature). */
const MINERAL_ORES: readonly MineralOre[] = galaxyOres.filter(isMineralOre)

/** One charted asteroid deposit: its grade and the ores it carries (positive share, galaxy ore order). */
export interface ChartedDeposit {
  fieldId: string
  grade: GalaxyGradeId
  ores: readonly MineralOre[]
}

/** A charted system's minerals. A system without an asteroid field has no signature and no deposits. */
export interface ChartedMinerals {
  /** The signature ore(s), or null (no asteroid field). */
  signature: SystemSignature | null
  /** Per ore carried by any graded deposit: the richest grade among the deposits that carry it. */
  grades: Readonly<Partial<Record<MineralOre, GalaxyGradeId>>>
  /** Every graded (asteroid) deposit, in field order. */
  deposits: readonly ChartedDeposit[]
}

export type MineralsKnowledge = 'uncharted' | ChartedMinerals

/** What the player knows about `systemId`'s minerals. An id the galaxy does not know is `'uncharted'`. */
export function mineralsKnowledge(reach: ReachState, galaxy: Galaxy, systemId: string): MineralsKnowledge {
  if (reach.knowledge[systemId] !== 'charted') return 'uncharted'
  const system = galaxyIndex(galaxy).systems.get(systemId)
  if (system === undefined) return 'uncharted'
  const grades: Partial<Record<MineralOre, GalaxyGradeId>> = {}
  const deposits: ChartedDeposit[] = []
  for (const field of system.fields) {
    const grade = field.grade
    if (grade === undefined) continue
    const weights = field.mineralWeights ?? field.oreWeights
    const ores = MINERAL_ORES.filter((ore) => (weights[ore] ?? 0) > 0)
    for (const ore of ores) {
      const best = grades[ore]
      if (best === undefined || galaxyGradeIds.indexOf(grade) > galaxyGradeIds.indexOf(best)) grades[ore] = grade
    }
    deposits.push({ fieldId: field.id, grade, ores })
  }
  const signature = system.signature === undefined ? null : { ...system.signature }
  return { signature, grades, deposits }
}
